import { useCallback, useState } from 'react'

import { getUsers, unseeAllAchievements } from '../../../../apiLayer'
import type { User as ApiUser } from '../../../../apiLayer/types'
import useAppState from '../../../../hooks/useAppState'

export interface UseUnseeAchievementsResult {
  usersQuery: string
  setUsersQuery: (q: string) => void
  users: ApiUser[]
  selectedUserId: string
  setSelectedUserId: (id: string) => void
  usersLoading: boolean
  achievementsLoading: boolean
  error: string
  result: string
  loadUsers: () => Promise<void>
  runUnsee: (userId?: string) => Promise<void>
}

/**
 * State + side effects for the "unsee all achievements" dev tool. Loads a
 * filtered user list, lets the operator pick one, and calls the backend reset.
 */
export function useUnseeAchievements(): UseUnseeAchievementsResult {
  const { state } = useAppState()

  const [usersQuery, setUsersQuery] = useState('')
  const [users, setUsers] = useState<ApiUser[]>([])
  const [selectedUserId, setSelectedUserId] = useState('')
  const [usersLoading, setUsersLoading] = useState(false)
  const [achievementsLoading, setAchievementsLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState('')

  const loadUsers = useCallback(async () => {
    setUsersLoading(true)
    setError('')
    setResult('')

    try {
      const res = await getUsers()
      if (!res.success) {
        setError(res.error || 'Failed to load users')
        setUsers([])
        return
      }

      const q = usersQuery.trim().toLowerCase()
      const filtered = q
        ? res.data.filter((u) => {
            const haystack = `${u.id} ${u.email} ${u.name}`.toLowerCase()
            return haystack.includes(q)
          })
        : res.data

      setUsers(filtered)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users')
      setUsers([])
    } finally {
      setUsersLoading(false)
    }
  }, [usersQuery])

  const runUnsee = useCallback(
    async (userId?: string) => {
      setAchievementsLoading(true)
      setError('')
      setResult('')

      try {
        const res = await unseeAllAchievements(state, {
          userId: userId || state.user?.id
        })
        if (!res.success) {
          setError(res.error || 'Failed to unsee achievements')
        } else {
          setResult(`Unseen achievements reset. unseenCount: ${res.data.unseenCount} (userId: ${res.data.userId})`)
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to unsee achievements')
      } finally {
        setAchievementsLoading(false)
      }
    },
    [state]
  )

  return {
    usersQuery,
    setUsersQuery,
    users,
    selectedUserId,
    setSelectedUserId,
    usersLoading,
    achievementsLoading,
    error,
    result,
    loadUsers,
    runUnsee
  }
}
